import { Directive, ElementRef, Injector, OnDestroy, OnInit } from '@angular/core';
import {
    FiTextItemMediaPanelAlignmentMenuItems,
    MediaItemAlignmentMenuItemAlignmentType,
} from './fi-text-item-media-panel-handler/fi-text-item-media-panel-alignment/fi-text-item-media-panel-alignment-menu-items';
import { BehaviorSubject, Subject } from 'rxjs';
import { eventFindInPath, isDefined } from '@fi-shared/utils';
import { takeUntil } from 'rxjs/operators';
import { FiTextItemMediaSettingsDialogComponent } from '@fi-app/utilities/insert-media/fi-text-item-media-panel-handler/fi-text-item-media-settings-dialog/fi-text-item-media-settings-dialog.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ITextItemMediaSettingsDialogData } from '@fi-shared/fi-text-item-helper/fi-text-item-media';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { AdvancedTextPanelMediaLinkService } from '@fi-shared/fi-text-panel/text-panel-services/advanced-text-panel-media-link.service';

@Directive()
export class FiMediaPanelHandlerBase implements OnInit, OnDestroy {
    protected currentItemMediaAlignmentClass: string;
    currentSelectedElement$ = new BehaviorSubject<HTMLElement>(null);

    protected panelWidth: number;
    protected elementRef: ElementRef;
    protected appTextService: AppTextService;
    protected fiDialogsService: FiDialogsService;
    protected advancedTextPanelMediaLinkService: AdvancedTextPanelMediaLinkService;
    protected lastTextItemMedia: HTMLElement;
    protected panelMinTopOffset: number;

    constructor(injector: Injector) {
        this.elementRef = injector.get(ElementRef);
        this.appTextService = injector.get(AppTextService);
        this.fiDialogsService = injector.get(FiDialogsService);
        this.advancedTextPanelMediaLinkService = injector.get(AdvancedTextPanelMediaLinkService);
    }

    ngOnInit(): void {
        this.panelWidth = this.elementRef.nativeElement.clientWidth;
        this.currentSelectedElement$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((value: HTMLElement) => {
            this.updatePanelPosition(value);
        });
    }

    protected openSettingsDialogBase(
        dialogData: ITextItemMediaSettingsDialogData,
    ): IFiDialogResult<ITextItemMediaSettingsDialogData> {
        return this.fiDialogsService.custom({
            headerText: this.appTextService.get('settings'),
            yesButtonText: this.appTextService.get('done'),
            noButtonText: this.appTextService.get('common.cancel'),
            contentComponentRef: FiTextItemMediaSettingsDialogComponent,
            contentComponentData: dialogData,
            config: {
                minWidth: '20%',
                autoFocus: false,
            },
        });
    }

    protected updatePanelPosition(currentMediaElement: HTMLElement): void {
        if (currentMediaElement) {
            const panelElement = this.elementRef.nativeElement;
            if (
                currentMediaElement.hasAttribute('fi-vimeo-preview-image') ||
                currentMediaElement.parentElement.hasAttribute('ccm-text-item-media-model')
            ) {
                currentMediaElement = currentMediaElement.parentElement;
            }
            setTimeout(() => {
                const borderWidth = +getComputedStyle(currentMediaElement).borderWidth.slice(0, -2);
                // NOTE: panelElement.offsetHeight * 3  - 3 is set to avoid covering bigger part of an image
                const isImageHeightSmall = currentMediaElement.offsetHeight < panelElement.offsetHeight * 3;
                if (
                    currentMediaElement.clientWidth < panelElement.offsetWidth &&
                    this.currentItemMediaAlignmentClass ===
                        FiTextItemMediaPanelAlignmentMenuItems.find((item) => {
                            return item.alignmentType === MediaItemAlignmentMenuItemAlignmentType.Left;
                        }).mediaItemClass
                ) {
                    panelElement.style.left = '0px';
                    panelElement.style.right = 'auto';
                } else if (
                    currentMediaElement.clientWidth < panelElement.offsetWidth &&
                    this.currentItemMediaAlignmentClass ===
                        FiTextItemMediaPanelAlignmentMenuItems.find((item) => {
                            return item.alignmentType === MediaItemAlignmentMenuItemAlignmentType.Right;
                        }).mediaItemClass
                ) {
                    panelElement.style.right = '0px';
                    panelElement.style.left = 'auto';
                } else {
                    panelElement.style.left =
                        currentMediaElement.offsetLeft +
                        Math.round((currentMediaElement.clientWidth - this.panelWidth) / 2) +
                        borderWidth +
                        'px';
                }
                const topOffset = isImageHeightSmall
                    ? currentMediaElement.offsetTop - panelElement.offsetHeight
                    : currentMediaElement.offsetTop;

                panelElement.style.top = `${
                    isDefined(this.panelMinTopOffset) ? Math.max(this.panelMinTopOffset, topOffset) : topOffset
                }px`;
            });
        }
    }

    protected toggleItemMediaBorderClass(isBorderShown: boolean): void {
        if (this.lastTextItemMedia) {
            this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ attachments: isBorderShown });
            if (isBorderShown) {
                this.lastTextItemMedia.classList.add('selected-item-media-border');
            } else {
                this.lastTextItemMedia.classList.remove('selected-item-media-border');
            }
        }
    }

    protected isIgnored(target: HTMLElement): boolean {
        const ignoreClasses: string[] = ['.cdk-overlay-backdrop', '.mat-dialog-container', '.cdk-overlay-pane'];
        return ignoreClasses.some((ignoreClass: string) => {
            return !!eventFindInPath({ target }, ignoreClass);
        });
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.toggleItemMediaBorderClass(false);
    }
}
